import Konva from "konva";
import {
  ChevronLeft,
  Download,
  Grid3x3,
  LayoutTemplate,
  Maximize2,
  Moon,
  Save,
  Sun,
  Trash2,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line as KonvaLine, Layer, Rect, Stage } from "react-konva";
import type { PlayerRow } from "../../repos/players.js";
import { DrawingLayer } from "../canvas-kit/drawing/drawing-layer.jsx";
import type {
  DrawStyle,
  DrawTool,
  Shape,
  Stroke,
  TextAnnotation,
} from "../canvas-kit/drawing/types.js";
import { pointInPolygon, useDrawing } from "../canvas-kit/drawing/use-drawing.js";
import {
  exportRequestToPdf,
  type PageDef,
  renderRequestToPngs,
  tileBoardAtScale,
  tileGridForBoard,
} from "../canvas-kit/export/index.js";
import { usePinchZoom } from "../canvas-kit/stage/use-pinch-zoom.js";
import { useConfirm } from "../canvas-kit/ui/confirm-dialog.jsx";
import { ContextMenu } from "../canvas-kit/ui/context-menu.jsx";
import {
  ExportDialog,
  type ExportDialogBuildArgs,
  type ExportLayoutPreview,
  type ExportMode,
} from "../canvas-kit/ui/export-dialog.jsx";
import { LabelDialog } from "../canvas-kit/ui/label-dialog.jsx";
import { type PaletteAnchor, ToolPalette } from "../canvas-kit/ui/tool-palette/tool-palette.jsx";
import { UnsavedChangesDialog } from "../canvas-kit/ui/unsaved-changes-dialog.jsx";
import { useExportSheet } from "../export-sheet/index.js";
import { CardDialog } from "./card-dialog.jsx";
import { DotGridLayer } from "./dot-grid-layer.jsx";
import { whiteboardRenderer } from "./render-snapshot.js";
import {
  type CardAccent,
  type CardWidget,
  emptyWhiteboardSnapshot,
  type PlayerWidget,
  type StickyColorId,
  type StickyNote as StickyNoteModel,
  type WhiteboardSnapshot,
  type Widget,
} from "./snapshot.js";
import { StickiesLayer } from "./stickies-layer.jsx";
import { WHITEBOARD_TEMPLATES } from "./templates.js";
import {
  WHITEBOARD_PALETTE_STYLE_TOOLS,
  WHITEBOARD_TOOLS,
  type WhiteboardToolMode,
} from "./tools.js";
import { WidgetsLayer } from "./widgets-layer.jsx";

if (typeof window !== "undefined") {
  Konva.hitOnDragEnabled = true;
  // @ts-expect-error captureTouchEventsEnabled exists at runtime, not in types
  Konva.captureTouchEventsEnabled = true;
}

interface WhiteboardEditorProps {
  name: string;
  snapshot: WhiteboardSnapshot;
  saving?: boolean;
  /** Roster used by the "Spieler"-tool picker. Optional → empty list. */
  players?: readonly PlayerRow[];
  onSave: (next: { name: string; snapshot: WhiteboardSnapshot }) => void;
  onDelete?: () => void;
  onClose: () => void;
}

/**
 * Whiteboard editor — MVP skeleton.
 *
 * Reuses every canvas-kit primitive: `usePinchZoom` for pan + zoom,
 * `useDrawing` + `DrawingLayer` for the pen/line/arrow/rect/circle/lasso
 * tool stack, `LabelDialog` for the text tool prompt and `ToolPalette`
 * for the floating tool dock.
 *
 * State lives entirely in `snap` (current `WhiteboardSnapshot`). Dirty
 * tracking is value-based: any user edit marks the board dirty until the
 * next `onSave`.
 */
export function WhiteboardEditor({
  name,
  snapshot,
  saving,
  players,
  onSave,
  onDelete,
  onClose,
}: WhiteboardEditorProps) {
  const [draftName, setDraftName] = useState(name);
  const [snap, setSnap] = useState<WhiteboardSnapshot>(snapshot);
  const [dirty, setDirty] = useState(false);
  const [tool, setTool] = useState<WhiteboardToolMode>("select");

  const playerById = useMemo(() => {
    const m = new Map<string, PlayerRow>();
    for (const p of players ?? []) m.set(p.id, p);
    return m;
  }, [players]);
  const [style, setStyle] = useState<DrawStyle>({
    color: "#ffffff",
    width: 4,
    dashed: false,
  });
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [textDialog, setTextDialog] = useState<{
    initialValue: string;
    textId: string | null;
    /** Board-space position for fresh inserts. */
    x?: number;
    y?: number;
  } | null>(null);
  const [paletteAnchor, setPaletteAnchor] = useState<PaletteAnchor>("right");
  const [paletteOpen, setPaletteOpen] = useState(true);
  const templateBtnRef = useRef<HTMLButtonElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { openExportSheet, sheet: exportSheet } = useExportSheet();
  /** Confirm-on-exit dialog visibility (P3). */
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  /** Generic confirm dialog for destructive/destructive-ish actions (template apply). */
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [templateMenu, setTemplateMenu] = useState<{
    screenX: number;
    screenY: number;
  } | null>(null);
  /** Dialog for editing a sticky note's body. id=null inserts a new sticky. */
  const [stickyDialog, setStickyDialog] = useState<{
    stickyId: string | null;
    initialValue: string;
    color: StickyColorId;
    /** Insert position (board coords) when stickyId is null. */
    x?: number;
    y?: number;
  } | null>(null);
  /** Editor for card / Übungsblock widgets. id=null inserts a new card. */
  const [cardDialog, setCardDialog] = useState<{
    widgetId: string | null;
    initialTitle: string;
    initialBody: string;
    initialAccent: CardAccent;
    /** Insert position (board coords) when widgetId is null. */
    x?: number;
    y?: number;
  } | null>(null);
  /** Player roster popover anchored at the click point. */
  const [playerPicker, setPlayerPicker] = useState<{
    screenX: number;
    screenY: number;
    boardX: number;
    boardY: number;
  } | null>(null);

  // Reset internal state if a different board snapshot is loaded — but never
  // overwrite unsaved edits. A periodic background pull (sync engine) may push
  // a fresh snapshot reference even when the on-disk JSON is unchanged; in
  // that case the route already memoises by `snapshot_json`, but we still
  // guard with `dirty` here as a belt-and-braces protection so live edits
  // are never silently discarded mid-session.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (dirtyRef.current) return;
    setSnap(snapshot);
    setDraftName(name);
    setDirty(false);
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot]);

  // ── Stage size + viewport ────────────────────────────────────
  const stageRef = useRef<Konva.Stage | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  // Filled in below once drawing/stage refs exist — aborts whatever the first
  // finger started when a two-finger pinch/pan gesture begins.
  const gestureCancelRef = useRef<() => void>(() => {});
  const { viewport, setViewport, reset, containerRef, onWheel, isGesturing, gesturing } =
    usePinchZoom({
      minScale: 0.2,
      maxScale: 6,
      initial: snapshot.viewport,
      onGestureStart: () => gestureCancelRef.current(),
    });

  // Restore the persisted viewport whenever a different board is loaded.
  // We track the board identity via the snapshot reference (the route already
  // memoises by `snapshot_json`, so this fires once per actual open). Without
  // this effect the editor would always reset to {scale:1,x:0,y:0} despite the
  // saved value being available, because `usePinchZoom`'s initial state is
  // only read once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (snapshot.viewport) setViewport(snapshot.viewport);
  }, [snapshot, setViewport]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setStageSize({ width: rect.width, height: rect.height });
    });
    ro.observe(el);
    const rect = el.getBoundingClientRect();
    setStageSize({ width: rect.width, height: rect.height });
    return () => ro.disconnect();
  }, [containerRef]);

  // ── Mutators (always mark dirty) ─────────────────────────────
  const mutate = useCallback((fn: (prev: WhiteboardSnapshot) => WhiteboardSnapshot) => {
    setSnap((prev) => fn(prev));
    setDirty(true);
  }, []);

  const addStroke = useCallback(
    (st: Stroke) => mutate((prev) => ({ ...prev, strokes: [...prev.strokes, st] })),
    [mutate],
  );
  const addShape = useCallback(
    (sh: Shape) => mutate((prev) => ({ ...prev, shapes: [...prev.shapes, sh] })),
    [mutate],
  );
  const addText = useCallback(
    (tx: TextAnnotation) => mutate((prev) => ({ ...prev, texts: [...prev.texts, tx] })),
    [mutate],
  );
  const updateShape = useCallback(
    (id: string, patch: { x?: number; y?: number; w?: number; h?: number }) =>
      mutate((prev) => ({
        ...prev,
        shapes: prev.shapes.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      })),
    [mutate],
  );
  // Live updates while a drag is in flight — same shape change, but committed
  // every animation frame so the user sees the geometry follow the pointer.
  // We still pipe through `mutate` so the dirty flag tracks correctly.
  const updateShapeLive = updateShape;

  const updateText = useCallback(
    (id: string, patch: { x?: number; y?: number; value?: string }) =>
      mutate((prev) => ({
        ...prev,
        texts: prev.texts.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      })),
    [mutate],
  );

  const addSticky = useCallback(
    (note: StickyNoteModel) => mutate((prev) => ({ ...prev, stickies: [...prev.stickies, note] })),
    [mutate],
  );
  const updateSticky = useCallback(
    (id: string, patch: Partial<Pick<StickyNoteModel, "x" | "y" | "value" | "color">>) =>
      mutate((prev) => ({
        ...prev,
        stickies: prev.stickies.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      })),
    [mutate],
  );

  const addWidget = useCallback(
    (w: Widget) => mutate((prev) => ({ ...prev, widgets: [...prev.widgets, w] })),
    [mutate],
  );
  const updateWidget = useCallback(
    (id: string, patch: Partial<Widget>) =>
      mutate((prev) => ({
        ...prev,
        widgets: prev.widgets.map((w) => (w.id === id ? ({ ...w, ...patch } as Widget) : w)),
      })),
    [mutate],
  );

  // ── Group drag (multi-select) ────────────────────────────────
  // When the user lassos several elements and starts dragging one of
  // them, every other selected entity should follow by the same delta. Mirrors
  // the token group-drag pattern in `apps/app/src/features/tactics/editor.tsx`,
  // but extended here to cover shapes, texts, stickies and widgets — the
  // user expects every selectable kind to participate.
  const groupAnchorsRef = useRef<Map<
    string,
    {
      kind: "sticky" | "widget" | "shape" | "text";
      x: number;
      y: number;
    }
  > | null>(null);
  const handleGroupDragStart = useCallback(
    (id: string, x: number, y: number) => {
      if (selectedIds.size <= 1 || !selectedIds.has(id)) {
        groupAnchorsRef.current = null;
        return;
      }
      const anchors = new Map<
        string,
        {
          kind: "sticky" | "widget" | "shape" | "text";
          x: number;
          y: number;
        }
      >();
      for (const s of snap.stickies) {
        if (selectedIds.has(s.id)) anchors.set(s.id, { kind: "sticky", x: s.x, y: s.y });
      }
      for (const w of snap.widgets) {
        if (selectedIds.has(w.id)) anchors.set(w.id, { kind: "widget", x: w.x, y: w.y });
      }
      for (const sh of snap.shapes) {
        if (selectedIds.has(sh.id)) anchors.set(sh.id, { kind: "shape", x: sh.x, y: sh.y });
      }
      for (const tx of snap.texts) {
        if (selectedIds.has(tx.id)) anchors.set(tx.id, { kind: "text", x: tx.x, y: tx.y });
      }
      // Re-anchor the dragged node at its live position so deltas stay 0
      // at the start of the gesture.
      const cur = anchors.get(id);
      if (cur) anchors.set(id, { ...cur, x, y });
      groupAnchorsRef.current = anchors;
    },
    [selectedIds, snap.stickies, snap.widgets, snap.shapes, snap.texts],
  );
  const handleGroupDragMove = useCallback((id: string, x: number, y: number) => {
    const anchors = groupAnchorsRef.current;
    if (!anchors) return;
    const a = anchors.get(id);
    if (!a) return;
    const dx = x - a.x;
    const dy = y - a.y;
    const stage = stageRef.current;
    if (!stage) return;
    for (const [otherId, anchor] of anchors) {
      if (otherId === id) continue;
      const prefix =
        anchor.kind === "sticky"
          ? "wb-sticky-"
          : anchor.kind === "widget"
            ? "wb-widget-"
            : anchor.kind === "shape"
              ? "wb-shape-"
              : "wb-text-";
      const node = stage.findOne(`#${prefix}${otherId}`);
      if (node) node.position({ x: anchor.x + dx, y: anchor.y + dy });
    }
  }, []);
  const handleGroupDragEnd = useCallback(
    (id: string, x: number, y: number) => {
      const anchors = groupAnchorsRef.current;
      if (!anchors || anchors.size <= 1) {
        groupAnchorsRef.current = null;
        return;
      }
      const a = anchors.get(id);
      if (!a) {
        groupAnchorsRef.current = null;
        return;
      }
      const dx = x - a.x;
      const dy = y - a.y;
      // Commit all moves in one mutation so undo works as a single step.
      mutate((prev) => ({
        ...prev,
        stickies: prev.stickies.map((s) => {
          const an = anchors.get(s.id);
          return an && an.kind === "sticky" ? { ...s, x: an.x + dx, y: an.y + dy } : s;
        }),
        widgets: prev.widgets.map((w) => {
          const an = anchors.get(w.id);
          return an && an.kind === "widget" ? ({ ...w, x: an.x + dx, y: an.y + dy } as Widget) : w;
        }),
        shapes: prev.shapes.map((sh) => {
          const an = anchors.get(sh.id);
          return an && an.kind === "shape" ? { ...sh, x: an.x + dx, y: an.y + dy } : sh;
        }),
        texts: prev.texts.map((tx) => {
          const an = anchors.get(tx.id);
          return an && an.kind === "text" ? { ...tx, x: an.x + dx, y: an.y + dy } : tx;
        }),
      }));
      groupAnchorsRef.current = null;
    },
    [mutate],
  );

  const deleteSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    mutate((prev) => ({
      ...prev,
      strokes: prev.strokes.filter((s) => !selectedIds.has(s.id)),
      shapes: prev.shapes.filter((s) => !selectedIds.has(s.id)),
      texts: prev.texts.filter((t) => !selectedIds.has(t.id)),
      stickies: prev.stickies.filter((s) => !selectedIds.has(s.id)),
      widgets: prev.widgets.filter((w) => !selectedIds.has(w.id)),
    }));
    setSelectedIds(new Set());
  }, [selectedIds, mutate]);

  // ── Drawing controller ───────────────────────────────────────
  const drawTool: DrawTool | null = useMemo(
    () =>
      tool === "pen" ||
      tool === "line" ||
      tool === "arrow" ||
      tool === "rect" ||
      tool === "circle" ||
      tool === "lasso"
        ? tool
        : null,
    [tool],
  );

  const drawing = useDrawing({
    tool: drawTool,
    style,
    onCommitStroke: addStroke,
    onCommitShape: addShape,
    onCommitLasso: (path) => {
      const hits = new Set<string>();
      for (const sh of snap.shapes) {
        const cx = sh.kind === "circle" ? sh.x : sh.x + sh.w / 2;
        const cy = sh.kind === "circle" ? sh.y : sh.y + sh.h / 2;
        if (pointInPolygon(cx, cy, path)) hits.add(sh.id);
      }
      for (const tx of snap.texts) {
        if (pointInPolygon(tx.x, tx.y, path)) hits.add(tx.id);
      }
      for (const sk of snap.stickies) {
        const cx = sk.x + sk.w / 2;
        const cy = sk.y + sk.h / 2;
        if (pointInPolygon(cx, cy, path)) hits.add(sk.id);
      }
      for (const w of snap.widgets) {
        const cx = w.x + w.w / 2;
        const cy = w.y + w.h / 2;
        if (pointInPolygon(cx, cy, path)) hits.add(w.id);
      }
      for (const st of snap.strokes) {
        for (let i = 0; i < st.points.length; i += 2) {
          if (pointInPolygon(st.points[i]!, st.points[i + 1]!, path)) {
            hits.add(st.id);
            break;
          }
        }
      }
      setSelectedIds(hits);
      // Auto-switch back to the select tool so the user can immediately drag
      // the freshly-lassoed group — matches the tactics editor behaviour.
      setTool("select");
    },
  });

  // ── Pointer routing ──────────────────────────────────────────
  const onStagePointerDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      // Two-finger pinch/pan in flight: the 2nd finger's touchstart lands
      // AFTER our pointerdown-based gesture start (pointer events precede
      // touch events) — without this bail-out it would start a fresh stroke
      // draft / insert dialog mid-gesture.
      if (isGesturing()) return;
      if (drawTool) {
        drawing.onPointerDown(e);
        return;
      }
      if (tool === "text") {
        // Tap on empty area → open insert dialog
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const tr = stage.getAbsoluteTransform().copy().invert();
        const pt = tr.point(pos);
        setTextDialog({
          initialValue: "",
          textId: null,
          x: pt.x,
          y: pt.y,
        });
        return;
      }
      if (tool === "sticky") {
        // Tap on empty area → spawn a new sticky and open its editor.
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const tr = stage.getAbsoluteTransform().copy().invert();
        const pt = tr.point(pos);
        setStickyDialog({
          stickyId: null,
          initialValue: "",
          color: "yellow",
          // Centre the sticky on the click for a more natural feel.
          x: pt.x - 110,
          y: pt.y - 90,
        });
        return;
      }
      if (tool === "card") {
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const tr = stage.getAbsoluteTransform().copy().invert();
        const pt = tr.point(pos);
        setCardDialog({
          widgetId: null,
          initialTitle: "",
          initialBody: "",
          initialAccent: "lime",
          x: pt.x - 140,
          y: pt.y - 90,
        });
        return;
      }
      if (tool === "player") {
        const stage = e.target.getStage();
        if (!stage) return;
        // Allow the picker to open even if the click lands on a non-listening
        // shape (e.g. a sticky/widget body) — we treat any tap in `player`
        // mode as "insert here" since drag/select is disabled anyway.
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const tr = stage.getAbsoluteTransform().copy().invert();
        const pt = tr.point(pos);
        // Native (e.g. PointerEvent / TouchEvent) for screen-space anchor.
        const native = e.evt as MouseEvent | TouchEvent;
        const screenX =
          native instanceof MouseEvent ? native.clientX : (native.touches[0]?.clientX ?? 0);
        const screenY =
          native instanceof MouseEvent ? native.clientY : (native.touches[0]?.clientY ?? 0);
        setPlayerPicker({
          screenX,
          screenY: screenY + 96,
          boardX: pt.x - 130,
          boardY: pt.y - 48,
        });
        return;
      }
      // Select: clear selection on empty-area tap
      if (tool === "select") {
        const stage = e.target.getStage();
        if (e.target === stage || e.target.getType() === "Layer") {
          setSelectedIds(new Set());
        }
      }
    },
    [tool, drawTool, drawing, isGesturing],
  );

  const onStagePointerMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (isGesturing()) return;
      if (drawTool) drawing.onPointerMove(e);
    },
    [drawTool, drawing, isGesturing],
  );

  const onStagePointerUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (drawTool) drawing.onPointerUp(e);
    },
    [drawTool, drawing],
  );

  // When a second finger lands (pinch/pan), abort everything the first finger
  // started: in-flight stroke drafts, the draggable-Stage background pan and
  // any element drag — otherwise the gesture draws lines / moves elements as
  // a side effect.
  useEffect(() => {
    gestureCancelRef.current = () => {
      drawing.cancel();
      const stage = stageRef.current;
      if (stage) {
        if (stage.isDragging()) stage.stopDrag();
        for (const node of stage.find((n: Konva.Node) => n.isDragging())) {
          node.stopDrag();
        }
      }
    };
  }, [drawing.cancel]);

  const handleSelect = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
  }, []);

  const handleTextDblClick = useCallback(
    (id: string) => {
      const tx = snap.texts.find((t) => t.id === id);
      if (!tx) return;
      setTextDialog({ initialValue: tx.value, textId: id });
    },
    [snap.texts],
  );

  const handleTextDialogConfirm = useCallback(
    ({ label }: { label: string; number?: string }) => {
      if (!textDialog) return;
      const value = label.trim();
      if (!value) {
        setTextDialog(null);
        return;
      }
      if (textDialog.textId) {
        updateText(textDialog.textId, { value });
      } else if (typeof textDialog.x === "number" && typeof textDialog.y === "number") {
        addText({
          id: crypto.randomUUID(),
          x: textDialog.x,
          y: textDialog.y,
          value,
          color: style.color,
          fontSize: 28,
        });
      }
      setTextDialog(null);
    },
    [textDialog, updateText, addText, style.color],
  );

  // Open the sticky editor for an existing sticky (double-tap).
  const handleStickyEdit = useCallback(
    (id: string) => {
      const sk = snap.stickies.find((s) => s.id === id);
      if (!sk) return;
      setStickyDialog({
        stickyId: id,
        initialValue: sk.value,
        color: sk.color,
      });
    },
    [snap.stickies],
  );

  const handleStickyDialogConfirm = useCallback(
    ({ label }: { label: string; number?: string }) => {
      if (!stickyDialog) return;
      const value = label;
      if (stickyDialog.stickyId) {
        updateSticky(stickyDialog.stickyId, { value });
      } else if (typeof stickyDialog.x === "number" && typeof stickyDialog.y === "number") {
        addSticky({
          id: crypto.randomUUID(),
          x: stickyDialog.x,
          y: stickyDialog.y,
          w: 220,
          h: 180,
          value,
          color: stickyDialog.color,
          fontSize: 26,
        });
      }
      setStickyDialog(null);
    },
    [stickyDialog, updateSticky, addSticky],
  );

  // Widget editing — double-tap on a widget opens the right editor based
  // on its `kind` (player → roster picker, card → CardDialog).
  const handleWidgetEdit = useCallback(
    (id: string) => {
      const w = snap.widgets.find((x) => x.id === id);
      if (!w) return;
      if (w.kind === "card") {
        setCardDialog({
          widgetId: w.id,
          initialTitle: w.title,
          initialBody: w.body,
          initialAccent: w.accent,
        });
        return;
      }
      // For player widgets we re-open the roster picker anchored at the
      // widget's centre so the coach can swap which player the card refers
      // to in one tap.
      const centerScreen = stageRef.current?.container().getBoundingClientRect();
      setPlayerPicker({
        screenX: centerScreen ? centerScreen.left + centerScreen.width / 2 : 200,
        screenY: centerScreen ? centerScreen.top + 200 : 240,
        boardX: w.x,
        boardY: w.y,
      });
      // Remember which widget we're editing so the picker patches in place
      // rather than inserting (encoded via widgetId === playerId below).
      setEditingWidgetId(w.id);
    },
    [snap.widgets],
  );

  const handleCardDialogConfirm = useCallback(
    ({ title, body, accent }: { title: string; body: string; accent: CardAccent }) => {
      if (!cardDialog) return;
      if (cardDialog.widgetId) {
        updateWidget(cardDialog.widgetId, {
          title,
          body,
          accent,
        } as Partial<CardWidget>);
      } else if (typeof cardDialog.x === "number" && typeof cardDialog.y === "number") {
        addWidget({
          kind: "card",
          id: crypto.randomUUID(),
          x: cardDialog.x,
          y: cardDialog.y,
          w: 280,
          h: 180,
          title,
          body,
          accent,
        });
      }
      setCardDialog(null);
    },
    [cardDialog, updateWidget, addWidget],
  );

  // Tracks the widget id when the player picker is re-opened to replace
  // an existing player widget (vs. inserting a fresh one).
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null);

  const handlePlayerPick = useCallback(
    (player: PlayerRow) => {
      if (!playerPicker) return;
      if (editingWidgetId) {
        updateWidget(editingWidgetId, {
          playerId: player.id,
          name: player.name,
          position: player.position,
          shirt: player.shirt_number,
        } as Partial<PlayerWidget>);
      } else {
        addWidget({
          kind: "player",
          id: crypto.randomUUID(),
          x: playerPicker.boardX,
          y: playerPicker.boardY,
          w: 280,
          h: 96,
          playerId: player.id,
          name: player.name,
          position: player.position,
          shirt: player.shirt_number,
        });
      }
      setPlayerPicker(null);
      setEditingWidgetId(null);
    },
    [playerPicker, editingWidgetId, addWidget, updateWidget],
  );

  // Apply a whiteboard template — replaces the current content. Asks for
  // confirmation if the user has unsaved changes so they don't blow away
  // in-progress work by accident.
  const applyTemplate = useCallback(
    (templateId: string) => {
      const tpl = WHITEBOARD_TEMPLATES.find((t) => t.id === templateId);
      if (!tpl) return;
      if (
        dirty ||
        snap.strokes.length ||
        snap.shapes.length ||
        snap.texts.length ||
        snap.stickies.length ||
        snap.widgets.length
      ) {
        const ok = window.confirm(
          `Vorlage „${tpl.label}" anwenden? Der aktuelle Inhalt wird ersetzt.`,
        );
        if (!ok) return;
      }
      mutate(() => ({ ...tpl.build(), bg: snap.bg, grid: snap.grid }));
      setSelectedIds(new Set());
      setTemplateMenu(null);
    },
    [dirty, snap, mutate],
  );

  // ── Save / close ─────────────────────────────────────────────
  const handleSave = useCallback(() => {
    // Capture a thumbnail of the currently visible viewport so the overview
    // tile shows what the user just saved (ohne extra-Render, JPEG hält die
    // Data-URL klein genug für den Snapshot-JSON).
    let cover: string | null = snap.cover ?? null;
    try {
      const stage = stageRef.current;
      if (stage && stageSize.width > 4 && stageSize.height > 4) {
        const THUMB_W = 480;
        const pixelRatio = Math.max(0.4, Math.min(2, THUMB_W / Math.max(stageSize.width, 1)));
        cover = stage.toDataURL({
          x: 0,
          y: 0,
          width: stageSize.width,
          height: stageSize.height,
          pixelRatio,
          mimeType: "image/jpeg",
          quality: 0.72,
        });
      }
    } catch {
      /* canvas tainted or stage unavailable — ship without a cover update */
    }
    onSave({
      name: draftName.trim() || name,
      snapshot: { ...snap, cover, viewport: { ...viewport } },
    });
    setDirty(false);
  }, [draftName, name, snap, stageSize.width, stageSize.height, viewport, onSave]);

  /**
   * Close-intent handler (P3). If the board is clean, leave immediately.
   * Otherwise open the confirm-on-exit dialog and let the user choose
   * between "Speichern & Verlassen", "Verwerfen" and "Abbrechen".
   */
  const requestClose = useCallback(() => {
    if (!dirty) {
      onClose();
      return;
    }
    setConfirmExitOpen(true);
  }, [dirty, onClose]);

  // ── Background palette ───────────────────────────────────────
  const isDark = snap.bg === "dark";
  const bgFill = isDark ? "#101418" : "#f5f5f0";
  const gridColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";

  // ── Export ───────────────────────────────────────────────────
  // Compute a bounding board rectangle that covers every authored element,
  // with a default fallback that matches the visible viewport so an empty
  // whiteboard still exports a single sensible page.
  const exportBoundsBoard = useMemo(
    () => snapshotContentBounds(snap, stageSize),
    [snap, stageSize],
  );
  const buildExportPages = useCallback(
    ({ paper, orientation, scale, overlapMm }: ExportDialogBuildArgs): PageDef[] =>
      tileBoardAtScale({
        board: exportBoundsBoard,
        paper,
        orientation,
        scale,
        overlapMm,
        background: bgFill,
        idPrefix: "wb",
        title: "Whiteboard",
      }),
    [exportBoundsBoard, bgFill],
  );
  const exportModes = useMemo<ExportMode[]>(
    () => [{ id: "default", label: "Standard", supportsScale: true }],
    [],
  );
  const getExportLayoutPreview = useCallback(
    ({
      paper,
      orientation,
      scale,
      overlapMm,
    }: ExportDialogBuildArgs): ExportLayoutPreview | null => {
      const { cols, rows, pageBoardW, pageBoardH, stepBoardW, stepBoardH } = tileGridForBoard({
        board: exportBoundsBoard,
        paper,
        orientation,
        scale,
        overlapMm,
      });
      return {
        cols,
        rows,
        boardW: exportBoundsBoard.w,
        boardH: exportBoundsBoard.h,
        pageBoardW,
        pageBoardH,
        stepBoardW,
        stepBoardH,
        overlapMm,
      };
    },
    [exportBoundsBoard],
  );
  const handleExportConfirm = useCallback(
    async (req: Parameters<typeof exportRequestToPdf>[1]) => {
      const base = (draftName.trim() || name || "whiteboard").replace(/[^a-z0-9-_]+/gi, "-");
      if (req.format === "pdf") {
        const bytes = await exportRequestToPdf(snap, req, whiteboardRenderer);
        setExportOpen(false);
        await openExportSheet({
          bytes,
          filename: `${req.filename ?? base}.pdf`,
          mime: "application/pdf",
          title: "Whiteboard exportieren",
        });
      } else {
        const rendered = await renderRequestToPngs(snap, req, whiteboardRenderer);
        setExportOpen(false);
        if (rendered.length === 1 && rendered[0]) {
          await openExportSheet({
            bytes: rendered[0].pngBytes,
            filename: `${req.filename ?? base}.png`,
            mime: "image/png",
            title: "Whiteboard exportieren",
          });
        } else {
          // Multi-page PNG → legacy multi-download (ExportSheet handles
          // a single file at a time).
          const { downloadBytes } = await import("../canvas-kit/export/to-pdf.js");
          rendered.forEach((r, i) => {
            const suffix = rendered.length > 1 ? `-${i + 1}` : "";
            downloadBytes(r.pngBytes, `${req.filename ?? base}${suffix}.png`, "image/png");
          });
        }
      }
      setExportOpen(false);
    },
    [snap, draftName, name, openExportSheet],
  );

  const hint = HINT[tool];

  return (
    <div
      className="flex h-full flex-col gap-3 px-3 pb-3 pt-3 sm:px-4"
      style={{ touchAction: "none" }}
    >
      {/* Topbar */}
      <div className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-4 py-3">
        <button
          type="button"
          onClick={requestClose}
          className="inline-flex h-9 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          aria-label="Zurück zur Übersicht"
        >
          <ChevronLeft className="size-4" />
          Übersicht
        </button>
        <input
          value={draftName}
          onChange={(e) => {
            setDraftName(e.target.value);
            setDirty(true);
          }}
          className="h-9 min-w-45 flex-1 rounded-full border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow"
          placeholder="Whiteboard-Name…"
        />
        <button
          type="button"
          onClick={() =>
            mutate((prev) => ({
              ...prev,
              bg: prev.bg === "dark" ? "light" : "dark",
            }))
          }
          className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm hover:bg-white/10"
          aria-label="Hintergrund umschalten"
        >
          {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          {isDark ? "Hell" : "Dunkel"}
        </button>
        <button
          type="button"
          onClick={() =>
            mutate((prev) => ({
              ...prev,
              grid: { ...prev.grid, enabled: !prev.grid.enabled },
            }))
          }
          className={
            snap.grid.enabled
              ? "inline-flex h-9 items-center gap-1 rounded-full bg-white/15 px-3 text-body-sm"
              : "inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm hover:bg-white/10"
          }
          aria-pressed={snap.grid.enabled}
          aria-label="Punktraster umschalten"
        >
          <Grid3x3 className="size-4" />
          Raster
        </button>
        <button
          type="button"
          onClick={() => setPaletteOpen((v) => !v)}
          className={`inline-flex size-9 items-center justify-center rounded-full transition-all ${
            paletteOpen
              ? "bg-pitch-lime/15 text-pitch-lime ring-1 ring-pitch-lime/50 shadow-glow"
              : "bg-white/6 text-muted-foreground hover:bg-white/10"
          }`}
          title={paletteOpen ? "Werkzeuge ausblenden" : "Werkzeuge einblenden"}
          aria-pressed={paletteOpen}
          aria-label="Werkzeuge"
        >
          <Wrench className="size-4" />
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm hover:bg-white/10"
          aria-label="Ansicht zurücksetzen"
        >
          <Maximize2 className="size-4" />
        </button>
        <button
          ref={templateBtnRef}
          type="button"
          onClick={(e) => {
            const btn = e.currentTarget;
            // Toggle: close if already open, otherwise open anchored to the button.
            setTemplateMenu((prev) => {
              if (prev) return null;
              const r = btn.getBoundingClientRect();
              return { screenX: r.left + r.width / 2, screenY: r.bottom + 96 };
            });
          }}
          className={
            templateMenu
              ? "inline-flex h-9 items-center gap-1 rounded-full bg-white/15 px-3 text-body-sm"
              : "inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm hover:bg-white/10"
          }
          aria-pressed={!!templateMenu}
          aria-label="Vorlage einfügen"
        >
          <LayoutTemplate className="size-4" />
          Vorlage
        </button>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm hover:bg-white/10"
          aria-label="Exportieren"
        >
          <Download className="size-4" />
          Export
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-body-sm text-[#ffb1b8] hover:bg-[#3a1418]"
            aria-label="Whiteboard löschen"
          >
            <Trash2 className="size-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex h-9 items-center gap-2 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-60"
        >
          <Save className="size-4" />
          {saving ? "Speichere…" : dirty ? "Speichern" : "Gespeichert"}
        </button>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        onWheel={onWheel}
        className="relative flex-1 overflow-hidden rounded-3xl border border-white/10"
        style={{ background: bgFill, touchAction: "none" }}
      >
        {stageSize.width > 0 && stageSize.height > 0 && (
          <Stage
            ref={(node) => {
              stageRef.current = node;
            }}
            width={stageSize.width}
            height={stageSize.height}
            scaleX={viewport.scale}
            scaleY={viewport.scale}
            x={viewport.x}
            y={viewport.y}
            // During a two-finger gesture the canvas is navigation-only: no
            // hit-testing and no draggable Stage — a Konva-initiated stage
            // drag would otherwise fight the pinch handler for x/y (jitter).
            listening={!gesturing}
            onMouseDown={onStagePointerDown}
            onTouchStart={onStagePointerDown}
            onMouseMove={onStagePointerMove}
            onTouchMove={onStagePointerMove}
            onMouseUp={onStagePointerUp}
            onTouchEnd={onStagePointerUp}
            draggable={tool === "select" && selectedIds.size === 0 && !gesturing}
            onDragEnd={(e) => {
              if (e.target.getType() !== "Stage") return;
              setViewport({
                ...viewport,
                x: e.target.x(),
                y: e.target.y(),
              });
            }}
          >
            {snap.grid.enabled && (
              <Layer listening={false}>
                <DotGridLayer
                  size={snap.grid.size}
                  viewport={viewport}
                  stageW={stageSize.width}
                  stageH={stageSize.height}
                  color={gridColor}
                />
              </Layer>
            )}
            <Layer listening={tool === "select"}>
              <DrawingLayer
                strokes={snap.strokes}
                shapes={snap.shapes}
                texts={snap.texts}
                draft={drawing.draft}
                selectedIds={selectedIds}
                editable={tool === "select"}
                onSelectElement={handleSelect}
                onShapeDragStart={handleGroupDragStart}
                onMoveShape={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragEnd(id, x, y);
                  } else {
                    updateShape(id, { x, y });
                  }
                }}
                onMoveShapeLive={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragMove(id, x, y);
                  } else {
                    updateShapeLive(id, { x, y });
                  }
                }}
                onResizeShape={(id, patch) => updateShape(id, patch)}
                onResizeShapeLive={(id, patch) => updateShapeLive(id, patch)}
                onTextDragStart={handleGroupDragStart}
                onMoveTextLive={(id, x, y) => {
                  if (groupAnchorsRef.current) handleGroupDragMove(id, x, y);
                }}
                onMoveText={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragEnd(id, x, y);
                  } else {
                    updateText(id, { x, y });
                  }
                }}
                onTextDblClick={handleTextDblClick}
              />
            </Layer>
            <Layer listening={tool === "select"}>
              <StickiesLayer
                stickies={snap.stickies}
                selectedIds={selectedIds}
                draggable={tool === "select"}
                onSelect={handleSelect}
                onDragStart={handleGroupDragStart}
                onDragMove={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragMove(id, x, y);
                  } else {
                    updateSticky(id, { x, y });
                  }
                }}
                onDragEnd={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragEnd(id, x, y);
                  } else {
                    updateSticky(id, { x, y });
                  }
                }}
                onEdit={handleStickyEdit}
              />
            </Layer>
            <Layer listening={tool === "select"}>
              <WidgetsLayer
                widgets={snap.widgets}
                selectedIds={selectedIds}
                draggable={tool === "select"}
                playerById={playerById}
                onSelect={handleSelect}
                onDragStart={handleGroupDragStart}
                onDragMove={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragMove(id, x, y);
                  } else {
                    updateWidget(id, { x, y } as Partial<Widget>);
                  }
                }}
                onDragEnd={(id, x, y) => {
                  if (groupAnchorsRef.current) {
                    handleGroupDragEnd(id, x, y);
                  } else {
                    updateWidget(id, { x, y } as Partial<Widget>);
                  }
                }}
                onEdit={handleWidgetEdit}
              />
            </Layer>
            {drawing.lassoPath && drawing.lassoPath.length >= 4 && (
              <Layer listening={false}>
                <KonvaLine
                  points={drawing.lassoPath}
                  stroke="#97fc00"
                  strokeWidth={2.5}
                  dash={[10, 6]}
                  closed
                  fill="rgba(151,252,0,0.08)"
                />
              </Layer>
            )}
            {/* Invisible hit-rect under everything so empty-area taps fire on Stage */}
            <Layer listening={false}>
              <Rect x={-1e5} y={-1e5} width={2e5} height={2e5} />
            </Layer>
          </Stage>
        )}

        {/* Tool palette */}
        {paletteOpen && (
          <ToolPalette
            tools={WHITEBOARD_TOOLS}
            drawToolIds={WHITEBOARD_PALETTE_STYLE_TOOLS}
            mode={tool}
            onMode={(m) => {
              const next = m as WhiteboardToolMode;
              setTool(next);
              // Preserve selection when switching to select/lasso so the user
              // can drag a freshly-lassoed group right away. Mirrors tactics.
              if (next !== "select" && next !== "lasso") {
                setSelectedIds(new Set());
              }
            }}
            hasSelection={selectedIds.size > 0}
            onDeleteSelection={deleteSelected}
            style={style}
            onStyle={(patch) => setStyle((s) => ({ ...s, ...patch }))}
            anchor={paletteAnchor}
            onAnchor={setPaletteAnchor}
          />
        )}

        {/* Hint pill */}
        <div
          className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-center text-[11px] uppercase tracking-widest text-white/70 backdrop-blur"
          style={{
            bottom: paletteAnchor === "bottom" ? "6rem" : "0.75rem",
          }}
        >
          {hint}
        </div>
      </div>

      {textDialog && (
        <LabelDialog
          title={textDialog.textId ? "Text bearbeiten" : "Text hinzufügen"}
          initialLabel={textDialog.initialValue}
          initialNumber=""
          hideNumber
          multiline
          labelFieldTitle="Text"
          onConfirm={handleTextDialogConfirm}
          onClose={() => setTextDialog(null)}
        />
      )}

      {exportOpen && (
        <ExportDialog
          title="Whiteboard exportieren"
          modes={exportModes}
          buildPages={buildExportPages}
          getLayoutPreview={getExportLayoutPreview}
          defaultFilename={(draftName.trim() || name || "whiteboard").replace(
            /[^a-z0-9-_]+/gi,
            "-",
          )}
          onConfirm={handleExportConfirm}
          onClose={() => setExportOpen(false)}
        />
      )}

      {exportSheet}

      {stickyDialog && (
        <LabelDialog
          title={stickyDialog.stickyId ? "Sticky bearbeiten" : "Sticky hinzufügen"}
          initialLabel={stickyDialog.initialValue}
          initialNumber=""
          hideNumber
          multiline
          labelFieldTitle="Notiz"
          onConfirm={handleStickyDialogConfirm}
          onClose={() => setStickyDialog(null)}
        />
      )}

      {templateMenu && (
        <ContextMenu
          title="Vorlagen"
          widthPx={240}
          screenX={templateMenu.screenX}
          screenY={templateMenu.screenY}
          ignoreRef={templateBtnRef}
          onClose={() => setTemplateMenu(null)}
          items={WHITEBOARD_TEMPLATES.map((t) => ({
            id: t.id,
            label: t.label,
            onSelect: () => applyTemplate(t.id),
          }))}
        />
      )}

      {cardDialog && (
        <CardDialog
          title={cardDialog.widgetId ? "Karte bearbeiten" : "Karte hinzufügen"}
          initialTitle={cardDialog.initialTitle}
          initialBody={cardDialog.initialBody}
          initialAccent={cardDialog.initialAccent}
          onConfirm={handleCardDialogConfirm}
          onClose={() => setCardDialog(null)}
        />
      )}

      {playerPicker && (
        <ContextMenu
          title={editingWidgetId ? "Spieler tauschen" : "Spieler einfügen"}
          widthPx={280}
          screenX={playerPicker.screenX}
          screenY={playerPicker.screenY}
          onClose={() => {
            setPlayerPicker(null);
            setEditingWidgetId(null);
          }}
          items={
            (players?.length ?? 0) > 0
              ? players!.map((p) => ({
                  id: p.id,
                  label:
                    p.shirt_number != null
                      ? `#${p.shirt_number}  ${p.name}${p.position ? `  · ${p.position}` : ""}`
                      : `${p.name}${p.position ? `  · ${p.position}` : ""}`,
                  onSelect: () => handlePlayerPick(p),
                }))
              : [
                  {
                    id: "empty",
                    label: "Keine Spieler im Kader",
                    onSelect: () => {
                      setPlayerPicker(null);
                      setEditingWidgetId(null);
                    },
                  },
                ]
          }
        />
      )}

      <UnsavedChangesDialog
        open={confirmExitOpen}
        saving={saving}
        onSaveAndClose={() => {
          handleSave();
          setConfirmExitOpen(false);
          onClose();
        }}
        onDiscard={() => {
          setConfirmExitOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmExitOpen(false)}
      />
      {confirmDialog}
    </div>
  );
}

const HINT: Record<WhiteboardToolMode, string> = {
  select: "Tippen: Auswählen · Fläche ziehen: Verschieben · 2 Finger: Zoom",
  lasso: "Ziehe eine Schlaufe um mehrere Elemente",
  pen: "Skizziere frei · 2 Finger: Zoom & Verschieben",
  line: "Ziehe eine gerade Linie",
  arrow: "Ziehe einen Pfeil",
  rect: "Ziehe ein Rechteck auf",
  circle: "Ziehe einen Kreis auf",
  text: "Tippe auf eine leere Stelle, um Text einzufügen",
  sticky: "Tippe auf eine leere Stelle, um einen Sticky einzufügen",
  player: "Tippe auf eine leere Stelle, um einen Spieler hinzuzufügen",
  card: "Tippe auf eine leere Stelle, um eine Karte einzufügen",
};

export function newWhiteboardSnapshot(): WhiteboardSnapshot {
  return emptyWhiteboardSnapshot();
}

/**
 * Compute a board-space rectangle that contains every authored element on
 * the whiteboard, padded for breathing room. Falls back to a viewport-sized
 * rectangle around the origin so empty whiteboards still produce a sensible
 * single export page.
 */
function snapshotContentBounds(
  snap: WhiteboardSnapshot,
  fallback: { width: number; height: number },
): { x: number; y: number; w: number; h: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const grow = (x: number, y: number) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  for (const s of snap.strokes) {
    for (let i = 0; i < s.points.length; i += 2) {
      grow(s.points[i] ?? 0, s.points[i + 1] ?? 0);
    }
  }
  for (const sh of snap.shapes) {
    if (sh.kind === "circle") {
      const r = Math.max(Math.abs(sh.w), 1);
      grow(sh.x - r, sh.y - r);
      grow(sh.x + r, sh.y + r);
    } else {
      grow(sh.x, sh.y);
      grow(sh.x + sh.w, sh.y + sh.h);
    }
  }
  for (const t of snap.texts) {
    grow(t.x, t.y);
    grow(t.x + t.value.length * t.fontSize * 0.6, t.y + t.fontSize * 1.4);
  }
  for (const sk of snap.stickies) {
    grow(sk.x, sk.y);
    grow(sk.x + sk.w, sk.y + sk.h);
  }
  for (const w of snap.widgets) {
    grow(w.x, w.y);
    grow(w.x + w.w, w.y + w.h);
  }
  if (!Number.isFinite(minX)) {
    const w = Math.max(fallback.width, 800);
    const h = Math.max(fallback.height, 600);
    return { x: -w / 2, y: -h / 2, w, h };
  }
  const padding = 80;
  return {
    x: minX - padding,
    y: minY - padding,
    w: maxX - minX + padding * 2,
    h: maxY - minY + padding * 2,
  };
}
